import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { isPersonalEmail, PERSONAL_EMAIL_MESSAGE } from '@/lib/email-validation';

const CRM_OPTIONS = ['None', 'HubSpot', 'Salesforce', 'Pipedrive', 'Other'] as const;
const TEAM_SIZES = ['1–5', '6–20', '21–50', '51–200', '200+'] as const;

export const CrmOptionalWaitlistForm = () => {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    company: '',
    team_size: '',
    current_crm: '',
    notes: '',
  });

  const set = (k: keyof typeof form, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.current_crm) {
      toast.error('Name, work email and current CRM are required.');
      return;
    }
    if (isPersonalEmail(form.email)) {
      toast.error(PERSONAL_EMAIL_MESSAGE);
      return;
    }
    setLoading(true);
    try {
      const { error } = await supabase.from('crm_optional_waitlist').insert({
        name: form.name.trim(),
        work_email: form.email.trim().toLowerCase(),
        company: form.company.trim() || null,
        team_size: form.team_size || null,
        current_crm: form.current_crm,
        notes: form.notes.trim() || null,
        source: 'crm-optional-page',
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        referrer: typeof document !== 'undefined' ? document.referrer : null,
      });
      if (error) throw error;

      // Notify founder — non-blocking
      supabase.functions
        .invoke('notify-founder', {
          body: {
            type: 'waitlist_signup',
            name: form.name,
            email: form.email,
            company: form.company,
            message: form.notes,
            metadata: {
              List: 'CRM-optional',
              'Team size': form.team_size || '—',
              'Current CRM': form.current_crm,
            },
          },
        })
        .catch(() => {});

      setSubmitted(true);
    } catch (err: any) {
      console.error('crm-optional waitlist error', err);
      toast.error(err?.message || 'Could not submit — please retry or email batiste@noq5.ai.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-card border border-border rounded-2xl p-8 text-center">
        <CheckCircle2 className="w-12 h-12 text-primary mx-auto mb-4" />
        <h3 className="text-2xl font-bold text-foreground mb-2">You're on the list.</h3>
        <p className="text-muted-foreground max-w-md mx-auto">
          We'll be in touch within one business day to set up your standalone workspace. No CRM required.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="bg-card border border-border rounded-2xl p-6 md:p-8 space-y-5">
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="cow-name">Full name *</Label>
          <Input id="cow-name" value={form.name} onChange={(e) => set('name', e.target.value)} required />
        </div>
        <div>
          <Label htmlFor="cow-email">Work email *</Label>
          <Input id="cow-email" type="email" value={form.email} onChange={(e) => set('email', e.target.value)} required />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="cow-company">Company</Label>
          <Input id="cow-company" value={form.company} onChange={(e) => set('company', e.target.value)} />
        </div>
        <div>
          <Label htmlFor="cow-team">Team size</Label>
          <select
            id="cow-team"
            value={form.team_size}
            onChange={(e) => set('team_size', e.target.value)}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          >
            <option value="">Select…</option>
            {TEAM_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      <div>
        <Label>Current CRM *</Label>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-2">
          {CRM_OPTIONS.map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => set('current_crm', opt)}
              className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                form.current_crm === opt
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-background border-border hover:border-primary/50 text-foreground'
              }`}
            >
              {opt}
            </button>
          ))}
        </div>
      </div>

      <div>
        <Label htmlFor="cow-notes">Anything we should know?</Label>
        <Textarea
          id="cow-notes"
          value={form.notes}
          onChange={(e) => set('notes', e.target.value)}
          rows={3}
          placeholder="Team, stack, when you want to start…"
        />
      </div>

      <Button type="submit" size="lg" disabled={loading} className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-bold py-6 text-lg">
        {loading ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Submitting…</> : 'Request early access'}
      </Button>
      <p className="text-xs text-muted-foreground text-center">
        €59 / user / month when we onboard you. No CRM required to start.
      </p>
    </form>
  );
};
